
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Browser } from '@capacitor/browser';

const saveAndShareFile = async (
  fileName: string,
  data: string,
  mimeType: string,
  isBase64: boolean = true
) => {
  try {
    if (Capacitor.isNativePlatform()) {
      const byteCharacters = isBase64 ? atob(data) : data;
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      
      // Save file to device's Downloads directory
      const savedToDownloads = await Filesystem.writeFile({
        path: `Download/${fileName}`,
        data: isBase64 ? data : btoa(data),
        directory: Directory.External,
        recursive: true
      });

      // Also save to cache for sharing
      const savedForSharing = await Filesystem.writeFile({
        path: fileName,
        data: isBase64 ? data : btoa(data),
        directory: Directory.Cache,
        recursive: true
      });

      // Share the file
      await Share.share({
        title: 'Exported Note',
        text: 'Here is your exported note',
        files: [savedForSharing.uri],
        dialogTitle: `Share ${fileName}`
      });

      return savedToDownloads.uri;
    } else {
      // Keep existing web handling
      const byteCharacters = isBase64 ? atob(data) : data;
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return url;
    }
  } catch (error) {
    console.error('Error saving and sharing file:', error);
    throw error;
  }
};

export const exportToPDF = async (content: string) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const maxWidth = pageWidth - (2 * margin);
    
    // Add logo to first page
    const img = new Image();
    img.src = '/lovable-uploads/f10795cf-dc59-4f21-912e-0e05da31e0ed.png';
    doc.addImage(img, 'PNG', margin, margin, 30, 10);
    
    let cursorY = 35; // Start after logo
    const lineHeight = 7;
    
    // Split content into lines that fit the page width
    const lines = doc.splitTextToSize(content, maxWidth);
    
    // Process each line
    for (let i = 0; i < lines.length; i++) {
      // Check if we need a new page
      if (cursorY + lineHeight > pageHeight - margin) {
        doc.addPage();
        cursorY = margin; // Reset cursor to top of new page
      }
      
      // Add the line
      doc.text(lines[i], margin, cursorY);
      cursorY += lineHeight;
    }
    
    const pdfData = doc.output('datauristring');
    const base64Data = pdfData.split(',')[1];
    const fileName = `note_${Date.now()}.pdf`;
    
    return await saveAndShareFile(fileName, base64Data, 'application/pdf', true);
  } catch (error) {
    console.error('Error exporting PDF:', error);
    throw new Error('Failed to export PDF');
  }
};

export const exportToExcel = async (content: string) => {
  try {
    // Split content into chunks that fit Excel cells better
    const MAX_CELL_LENGTH = 32767; // Excel's max cell content length
    const chunks = [];
    
    for (let i = 0; i < content.length; i += MAX_CELL_LENGTH) {
      chunks.push([content.slice(i, i + MAX_CELL_LENGTH)]);
    }
    
    const ws = XLSX.utils.aoa_to_sheet(chunks);
    
    // Adjust row heights for better readability
    const rowCount = chunks.length;
    if (!ws['!rows']) ws['!rows'] = [];
    for (let i = 0; i < rowCount; i++) {
      ws['!rows'][i] = { hpt: 30 }; // Set row height to 30 points
    }
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Note");
    
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'base64' });
    const fileName = `note_${Date.now()}.xlsx`;
    
    return await saveAndShareFile(
      fileName,
      excelBuffer,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      true
    );
  } catch (error) {
    console.error('Error exporting Excel:', error);
    throw new Error('Failed to export Excel');
  }
};

export const exportToDoc = async (content: string) => {
  try {
    // Split content into paragraphs for better formatting
    const paragraphs = content.split('\n').map(p => `<p>${p}</p>`).join('');
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>MindNote Export</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              line-height: 1.6;
              margin: 40px;
            }
            .logo {
              height: 40px;
              margin-bottom: 20px;
            }
            p {
              margin-bottom: 10px;
              page-break-inside: avoid;
            }
          </style>
        </head>
        <body>
          <img src="/lovable-uploads/f10795cf-dc59-4f21-912e-0e05da31e0ed.png" class="logo">
          <div>${paragraphs}</div>
        </body>
      </html>
    `;

    const fileName = `note_${Date.now()}.doc`;
    return await saveAndShareFile(fileName, htmlContent, 'application/msword', false);
  } catch (error) {
    console.error('Error exporting DOC:', error);
    throw new Error('Failed to export DOC');
  }
};

export const exportToMarkdown = async (title: string, content: string) => {
  try {
    const markdownContent = title ? `# ${title}\n\n${content}` : content;
    const fileName = `note_${Date.now()}.md`;
    return await saveAndShareFile(fileName, markdownContent, 'text/markdown', false);
  } catch (error) {
    console.error('Error exporting Markdown:', error);
    throw new Error('Failed to export Markdown');
  }
};
